import React, { useState, useEffect } from 'react';
import { useGetList } from 'react-admin';
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
} from '@mui/material';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css';
import { CampaignDataFilters } from './partials/CampaignDataFilters';
import { formatDate, formatDateToLocaleString } from '../../helper/date'; // theme css file
import { CampaignUtmPieChart } from './partials/CampaignUtmPieChart';
import { CampaignUtmBarChart } from './partials/CampaignUtmBarChart';
import { endOfDay, startOfDay } from 'date-fns';
import { getSelectedCompanyFilterValue } from '../../helper/filter';
import { Link, Navigate } from 'react-router-dom';
import { getUser } from '../../helper/user';
import { ROLES } from '../../constants/Roles';

const AnalyticsDashboardPage = () => {
  const [totalAmount, setTotalAmount] = useState(0);
  const [adParameter, setAdParameter] = useState('utm_source');
  const [selectionRangeType, setSelectionRangeType] = useState('day');
  const [selectionRange, setSelectionRange] = useState({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
    key: 'selection',
  });
  const defaultFilter = {
    inquiryCompany: getSelectedCompanyFilterValue(),
    'createdAt[before]': formatDate(selectionRange.endDate, true),
    'createdAt[after]': formatDate(selectionRange.startDate, false),
  };

  const allInquiries = useGetList('inquiries/data/chart', {
    filter: defaultFilter,
  });

  if (allInquiries.error) {
    console.error(allInquiries.error);
  }

  useEffect(() => {
    if (allInquiries.total !== null) {
      setTotalAmount(allInquiries.total);
    }
  }, [allInquiries]);

  const filteredInquiries = useGetList('inquiries/data/chart', {
    pagination: false,
    filter: { ...defaultFilter, ...{ 'exists[adCookie]': true } },
  });

  return (
    <Card sx={{ marginTop: 2 }}>
      <CardHeader
        title={`${totalAmount} Anfragen vom ${formatDateToLocaleString(
          selectionRange.startDate
        )} bis ${formatDateToLocaleString(selectionRange.endDate)}`}
        action={
          <CampaignDataFilters
            selectionRange={selectionRange}
            setSelectionRange={setSelectionRange}
            adParameter={adParameter}
            setAdParameter={setAdParameter}
            setSelectionRangeType={setSelectionRangeType}
          />
        }
      />
      <CardContent>
        <Grid container spacing={4} sx={{ p: 2 }}>
          <Grid item xs={6}>
            <CampaignUtmBarChart
              adParameter={adParameter}
              allInquiries={allInquiries}
              filteredInquiries={filteredInquiries}
              selectionRange={selectionRange}
              loading={allInquiries.isPending || filteredInquiries.isPending}
              selectedCompany={getSelectedCompanyFilterValue()}
            />
          </Grid>
          <Grid item xs={6}>
            <CampaignUtmPieChart
              adParameter={adParameter}
              allInquiries={allInquiries}
              filteredInquiries={filteredInquiries}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const CourseCreatorDashboardPage = () => {
  const { data: companies, isPending } = useGetList('companies', {
    pagination: { page: 1, perPage: 1 },
  });
  const hasAssignedCompany = companies && companies.length > 0;

  return (
    <Card sx={{ marginTop: 2 }}>
      <CardHeader title="Course entry" />
      <CardContent>
        {!isPending && hasAssignedCompany ? (
          <>
            <Typography sx={{ marginBottom: 2 }}>
              Select the company in the menu, then add a new unpublished course.
            </Typography>
            <Button component={Link} variant="contained" to="/courses/create">
              Add course
            </Button>
          </>
        ) : null}
        {!isPending && !hasAssignedCompany ? (
          <Typography>
            No company is assigned to this account. Please contact an administrator.
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
};

export const DashboardPage = () => {
  const user = getUser();
  if (user?.roles?.includes(ROLES.ROLE_SEO_EDITOR)) return <Navigate to="/courses" replace />;

  if (user?.roles?.includes(ROLES.ROLE_COURSE_CREATOR)) {
    return <CourseCreatorDashboardPage />;
  }

  return <AnalyticsDashboardPage />;
};
